import React, { useState } from 'react';
import { X, Search, ExternalLink, Plus, Tag } from 'lucide-react';
import { Resource } from '../types';
import './ResourceLibrary.css';

interface ResourceLibraryProps {
  onClose: () => void;
}

const DEFAULT_RESOURCES = {
  'DSA Patterns': [
    { id: 'dsa-1', title: 'Two Pointers Pattern', url: 'https://leetcode.com/discuss/study-guide/1688903/', type: 'article' as const },
    { id: 'dsa-2', title: 'Sliding Window', url: 'https://leetcode.com/discuss/study-guide/657507/', type: 'article' as const },
    { id: 'dsa-3', title: 'Dynamic Programming Patterns', url: 'https://leetcode.com/discuss/study-guide/1433252/', type: 'article' as const },
    { id: 'dsa-4', title: 'Graph Algorithms', url: 'https://leetcode.com/discuss/study-guide/655708/', type: 'article' as const },
    { id: 'dsa-5', title: 'Binary Search Patterns', url: 'https://leetcode.com/discuss/study-guide/786126/', type: 'article' as const }
  ],
  'System Design': [
    { id: 'sd-1', title: 'System Design Interview Guide', url: 'https://github.com/donnemartin/system-design-primer', type: 'article' as const },
    { id: 'sd-2', title: 'Grokking System Design', url: 'https://www.educative.io/courses/grokking-the-system-design-interview', type: 'video' as const },
    { id: 'sd-3', title: 'Design Patterns', url: 'https://refactoring.guru/design-patterns', type: 'article' as const },
    { id: 'sd-4', title: 'CAP Theorem Explained', url: 'https://www.youtube.com/watch?v=k-Yaq8AHlFA', type: 'video' as const }
  ],
  'CS Fundamentals': [
    { id: 'cs-1', title: 'Operating Systems Course', url: 'https://www.youtube.com/playlist?list=PLBlnK6fEyqRiVhbXDGLXDk_OQAeuVcp2O', type: 'video' as const },
    { id: 'cs-2', title: 'Computer Networks', url: 'https://www.youtube.com/playlist?list=PLBlnK6fEyqRgMCUAG0XRw78UA8qnv6jEx', type: 'video' as const },
    { id: 'cs-3', title: 'Database Systems', url: 'https://www.coursera.org/learn/database-systems', type: 'video' as const },
    { id: 'cs-4', title: 'Compiler Design', url: 'https://www.geeksforgeeks.org/compiler-design/', type: 'article' as const }
  ],
  'Company Specific': [
    { id: 'comp-1', title: 'Google Interview Process', url: 'https://www.youtube.com/watch?v=X5g0J_VwTSs', type: 'video' as const },
    { id: 'comp-2', title: 'Amazon Leadership Principles', url: 'https://www.amazon.jobs/content/en/our-workplace/leadership-principles', type: 'article' as const },
    { id: 'comp-3', title: 'Facebook Coding Interview', url: 'https://www.youtube.com/watch?v=wCl9kvQGHPI', type: 'video' as const },
    { id: 'comp-4', title: 'Microsoft Interview Guide', url: 'https://careers.microsoft.com/us/en/interviewtips', type: 'article' as const }
  ]
};

export const ResourceLibrary: React.FC<ResourceLibraryProps> = ({ onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [resources, setResources] = useState<Record<string, Resource[]>>(DEFAULT_RESOURCES);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newResource, setNewResource] = useState({ title: '', url: '', category: '' });

  const filteredResources = Object.entries(resources).reduce((acc, [category, items]) => {
    if (selectedCategory && category !== selectedCategory) return acc;
    
    const filtered = items.filter(resource =>
      resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.url.toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    if (filtered.length > 0) {
      acc[category] = filtered;
    }
    
    return acc;
  }, {} as Record<string, Resource[]>);

  const handleAddResource = () => {
    if (newResource.title && newResource.url && newResource.category) {
      const category = newResource.category;
      const resource: Resource = {
        id: `custom-${Date.now()}`,
        title: newResource.title,
        url: newResource.url,
        type: 'article',
        isCustom: true
      };
      
      setResources({
        ...resources,
        [category]: [...(resources[category] || []), resource]
      });
      
      setNewResource({ title: '', url: '', category: '' });
      setShowAddForm(false);
    }
  };

  const getResourceIcon = (type: Resource['type']) => {
    switch (type) {
      case 'video': return '🎥';
      case 'problem': return '💻';
      case 'template': return '📋';
      default: return '📄';
    }
  };

  return (
    <div className="resource-library-overlay" onClick={onClose}>
      <div className="resource-library-content" onClick={e => e.stopPropagation()}>
        <div className="resource-header">
          <h2>Resource Library</h2>
          <button onClick={onClose} className="close-button">
            <X size={20} />
          </button>
        </div>
        
        <div className="resource-controls">
          <div className="search-bar">
            <Search size={20} />
            <input
              type="text"
              placeholder="Search resources..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
          
          <button onClick={() => setShowAddForm(!showAddForm)} className="add-button">
            <Plus size={20} />
            Add Resource
          </button>
        </div>
        
        {showAddForm && (
          <div className="add-resource-form">
            <input
              type="text"
              placeholder="Resource title"
              value={newResource.title}
              onChange={e => setNewResource({ ...newResource, title: e.target.value })}
            />
            <input
              type="url"
              placeholder="URL"
              value={newResource.url}
              onChange={e => setNewResource({ ...newResource, url: e.target.value })}
            />
            <input
              type="text"
              placeholder="Category"
              value={newResource.category}
              onChange={e => setNewResource({ ...newResource, category: e.target.value })}
            />
            <button onClick={handleAddResource}>Add</button>
            <button onClick={() => setShowAddForm(false)}>Cancel</button>
          </div>
        )}
        
        <div className="category-tabs">
          <button
            className={`tab ${!selectedCategory ? 'active' : ''}`}
            onClick={() => setSelectedCategory(null)}
          >
            All
          </button>
          {Object.keys(resources).map(category => (
            <button
              key={category}
              className={`tab ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
        
        <div className="resources-list">
          {Object.entries(filteredResources).map(([category, items]) => (
            <div key={category} className="resource-category">
              <h3>{category}</h3>
              <div className="resource-items">
                {items.map(resource => (
                  <a
                    key={resource.id}
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="resource-item"
                  >
                    <span className="resource-icon">{getResourceIcon(resource.type)}</span>
                    <span className="resource-title">{resource.title}</span>
                    <ExternalLink size={16} className="external-icon" />
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
        
        <div className="quick-access">
          <h3>Quick Access</h3>
          <div className="quick-links">
            <a href="https://leetcode.com" target="_blank" rel="noopener noreferrer" className="quick-link">
              LeetCode
            </a>
            <a href="https://www.hackerrank.com" target="_blank" rel="noopener noreferrer" className="quick-link">
              HackerRank
            </a>
            <a href="https://www.pramp.com" target="_blank" rel="noopener noreferrer" className="quick-link">
              Pramp
            </a>
            <a href="https://www.geeksforgeeks.org" target="_blank" rel="noopener noreferrer" className="quick-link">
              GeeksforGeeks
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};